import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator, CommandShortcut,
} from '../ui/command';
import {
  LayoutDashboard, BarChart3, Heart, DollarSign, Target, Users,
  TrendingUp, FileText, Database, Share2, FileDown, Pin,
  GitCompare, BookOpen, Cog, Search, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import logger from '../../lib/logger';

/**
 * CommandPalette — Global Cmd+K (Ctrl+K) launcher.
 *
 * Ships a curated set of "go anywhere" + "do anything" actions
 * so power users never have to chase a button:
 *   • Navigate to any of the 18 standardized hero pages.
 *   • Trigger Quarterly Deck actions (Download, Share Link).
 *   • Pin the current page's hero stats to the deck.
 *   • Compare scenarios, build a business case, search docs.
 *
 * Mounts once at the dashboard root so the listener is wired
 * exactly once for the lifetime of the authenticated session.
 *
 * Hotkey behavior mirrors VS Code / Linear:
 *   Cmd+K (mac) / Ctrl+K (everyone else)  → toggle open
 *   Esc                                    → close
 */
const NAV_ITEMS = [
  { label: 'Home / Insights', path: '/', icon: LayoutDashboard, hint: 'Learning Intelligence Engine' },
  { label: 'ROI Health', path: '/roi-health', icon: DollarSign, hint: 'Average ROI · north-star' },
  { label: 'LEI Dashboard', path: '/lei-dashboard', icon: BarChart3, hint: 'Learning Effectiveness Index' },
  { label: 'PE Health', path: '/people-engine/health', icon: Users, hint: 'People Engine score' },
  { label: 'Maturity Health', path: '/maturity-health', icon: Target, hint: 'Org learning maturity' },
  { label: 'ROE Health', path: '/roe-health', icon: Heart, hint: 'Return on expectation' },
  { label: 'Navigation Signals', path: '/score-trends', icon: TrendingUp, hint: 'Journey signal trends' },
  { label: 'Saved Scenarios', path: '/scenarios', icon: FileText, hint: 'Your saved business cases' },
  { label: 'Solution Finder', path: '/solution-finder', icon: Sparkles, hint: 'Wizard from problem → ROI' },
  { label: 'Business Case Builder', path: '/business-case-builder', icon: FileText, hint: 'New prospectus' },
  { label: 'Comparison Analytics', path: '/comparison-analytics', icon: GitCompare, hint: 'A/B between cohorts' },
  { label: 'Value Conversion Analysis', path: '/gap-analysis', icon: TrendingUp, hint: 'Where value conversion broke down' },
  { label: 'Data Management', path: '/data-management', icon: Database, hint: 'Imports + anomalies' },
  { label: 'Anomaly Dashboard', path: '/anomalies', icon: TrendingUp, hint: 'Statistical outliers' },
  { label: 'Research Evidence', path: '/research', icon: BookOpen, hint: 'Citation library' },
  { label: 'LEI Reference', path: '/lei-reference', icon: BookOpen, hint: 'Measure definitions' },
  { label: 'Profile / Settings', path: '/profile', icon: Cog, hint: 'Account & preferences' },
];

const SEARCH_HINT = 'Type to search · ↑ ↓ to navigate · ↵ to select · Esc to close';

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [downloadingDeck, setDownloadingDeck] = useState(false);
  const [creatingShare, setCreatingShare] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Cmd+K / Ctrl+K hotkey. We register on window so it fires
  // even when focus is inside form fields — mirrors Linear/Notion.
  // The capture-phase preventDefault stops Chrome's address-bar
  // shortcut from stealing the keystroke.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const go = useCallback((path) => {
    setOpen(false);
    if (path) navigate(path);
  }, [navigate]);

  const handleDownloadDeck = useCallback(async () => {
    if (downloadingDeck) return;
    setDownloadingDeck(true);
    try {
      const res = await api.get('/quarterly-deck/pins/export-pdf', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : 'Quarterly_Impact_Deck.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Quarterly Deck downloaded');
      setOpen(false);
    } catch (err) {
      logger.error?.('cmdk download deck failed', err);
      const detail = err?.response?.data?.detail
        || (err?.response?.status === 404
          ? 'Pin a hero tile first — there\'s nothing to export yet.'
          : 'Could not generate PDF — try again.');
      toast.error(detail);
    } finally {
      setDownloadingDeck(false);
    }
  }, [downloadingDeck]);

  const handleCreateShare = useCallback(async () => {
    if (creatingShare) return;
    setCreatingShare(true);
    try {
      const res = await api.post('/quarterly-deck/pins/share', { expires_in_days: 60 });
      const url = res.data?.share_url;
      if (!url) {
        toast.error('Share link could not be created');
        return;
      }
      const absolute = url.startsWith('http') ? url : `${window.location.origin}${url}`;
      try {
        await navigator.clipboard.writeText(absolute);
        toast.success('Share link copied to clipboard', {
          description: 'Anyone with this link can view the deck for 60 days.',
        });
      } catch {
        toast.success('Share link created', { description: absolute });
      }
      setOpen(false);
    } catch (err) {
      logger.error?.('cmdk share failed', err);
      const detail = err?.response?.data?.detail || 'Could not create share link';
      toast.error(detail);
    } finally {
      setCreatingShare(false);
    }
  }, [creatingShare]);

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen} title="Command palette" description="Search pages and run quick actions">
        <CommandInput
          placeholder={`Search · ${SEARCH_HINT.split(' · ').slice(1).join(' · ')}`}
          data-testid="cmdk-input"
        />
        <CommandList data-testid="cmdk-list">
          <CommandEmpty>No matches. Try "ROI" or "scenarios".</CommandEmpty>

          <CommandGroup heading="Quick actions">
            <CommandItem
              onSelect={handleDownloadDeck}
              disabled={downloadingDeck}
              data-testid="cmdk-download-deck"
            >
              <FileDown className="mr-2 h-4 w-4" />
              <span>{downloadingDeck ? 'Generating…' : 'Download Quarterly Deck (PDF)'}</span>
            </CommandItem>
            <CommandItem
              onSelect={handleCreateShare}
              disabled={creatingShare}
              data-testid="cmdk-share-deck"
            >
              <Share2 className="mr-2 h-4 w-4" />
              <span>{creatingShare ? 'Creating…' : 'Create Quarterly Deck share link'}</span>
            </CommandItem>
            <CommandItem
              onSelect={() => go('/scenarios')}
              data-testid="cmdk-pin-scenarios"
            >
              <Pin className="mr-2 h-4 w-4" />
              <span>Open Saved Scenarios to pin a tile</span>
            </CommandItem>
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading="Navigate">
            {NAV_ITEMS.filter((n) => n.path !== location.pathname).map((n) => {
              const Icon = n.icon || Search;
              return (
                <CommandItem
                  key={n.path}
                  onSelect={() => go(n.path)}
                  data-testid={`cmdk-nav-${n.path.replace(/\//g, '-')}`}
                  value={`${n.label} ${n.hint || ''}`}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  <div className="flex flex-col">
                    <span>{n.label}</span>
                    {n.hint && (
                      <span className="text-[11px] text-slate-400">{n.hint}</span>
                    )}
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>

          <CommandSeparator />
          <CommandGroup heading="Tips">
            <CommandItem disabled>
              <Sparkles className="mr-2 h-4 w-4 text-indigo-500" />
              <span className="text-xs">{SEARCH_HINT}</span>
              <CommandShortcut>⌘K</CommandShortcut>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}

export default CommandPalette;
